import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import ImageUpload from "@/components/ImageUpload";
import { Flame, Shield, LogOut, Loader2, CheckCircle2, XCircle, Clock, Eye, Save, Store, Users, CreditCard, Tag, Plus, Pencil, Trash2, Search, MousePointerClick, Calendar, ExternalLink } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const MODULES = [
  { id: "custom_domain", label: "Custom domain" },
  { id: "themes", label: "Public-page themes" },
  { id: "analytics", label: "Analytics dashboard" },
  { id: "unlimited_items", label: "Unlimited offers & categories" },
];

function ReportsPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/reports").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  const exportData = async () => {
    const r = await api.get("/admin/export", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = "dealwisp-export.json"; a.click();
  };
  return (
    <div className="space-y-4" data-testid="reports-panel">
      <Button variant="outline" onClick={exportData} className="rounded-full border-white/15" data-testid="export-btn"><Save className="w-4 h-4 mr-1" /> Download data backup (JSON)</Button>
      <h3 className="font-display font-semibold">Abuse reports</h3>
      {rows === null ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : rows.length === 0 ? <p className="text-sm text-muted-foreground">No reports.</p> : rows.map((r) => (
        <div key={r.report_id} className="bg-card border border-white/10 rounded-lg p-4 text-sm">
          <a className="text-primary" href={`/b/${r.slug}`} target="_blank" rel="noreferrer">/b/{r.slug}</a> · <span className="text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
          <p className="mt-1">{r.reason}</p>{r.contact && <p className="text-xs text-muted-foreground">Contact: {r.contact}</p>}
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [settings, setSettings] = useState({});
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proofView, setProofView] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [paymentQ, setPaymentQ] = useState("");
  const [businessQ, setBusinessQ] = useState("");
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const emptyPlan = { id: "", name: "", price: 0, currency: "PKR", interval: "month", days: 30, order: 99, modules: [] };
  const [planOpen, setPlanOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [savingPlan, setSavingPlan] = useState(false);

  const load = async () => {
    const [st, p, b, s, pl] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/payments"), api.get("/admin/businesses"), api.get("/admin/payment-settings"), api.get("/admin/plans"),
    ]);
    setStats(st.data); setPayments(p.data); setBusinesses(b.data); setSettings(s.data || {}); setPlans(pl.data);
  };
  useEffect(() => { (async () => { try { await load(); } finally { setLoading(false); } })(); }, []);

  const searchPayments = async (q) => {
    const { data } = await api.get("/admin/payments", { params: q ? { q } : {} });
    setPayments(data);
  };
  const searchBusinesses = async (q) => {
    const { data } = await api.get("/admin/businesses", { params: q ? { q } : {} });
    setBusinesses(data);
  };

  const openUserDetail = async (userId) => {
    setUserDetail({}); // truthy placeholder so the dialog opens immediately with a loading state
    setUserDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/detail`);
      setUserDetail(data);
    } catch {
      toast.error("Couldn't load details");
      setUserDetail(null);
    } finally {
      setUserDetailLoading(false);
    }
  };

  const act = async (id, action) => {
    await api.post(`/admin/payments/${id}/${action}`);
    // Refresh the stat cards too (pending count, active subs, plan breakdown all shift on approve/reject),
    // but keep whatever payments search filter the admin currently has active rather than clearing it.
    const [st] = await Promise.all([api.get("/admin/stats"), searchPayments(paymentQ)]);
    setStats(st.data);
    toast.success(`Payment ${action}d`);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/payment-settings", settings);
      toast.success("Payment settings saved");
    } catch {
      toast.error("Failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const setS = (k) => (e) => setSettings((v) => ({ ...v, [k]: e.target?.value ?? e }));
  const handleLogout = async () => { await logout(); navigate("/login"); };

  const openNewPlan = () => { setEditingPlan(null); setPlanForm(emptyPlan); setPlanOpen(true); };
  const openEditPlan = (p) => { setEditingPlan(p); setPlanForm({ ...p }); setPlanOpen(true); };
  const savePlan = async () => {
    if (!planForm.name.trim()) return toast.error("Plan name required");
    setSavingPlan(true);
    try {
      const payload = { ...planForm, price: Number(planForm.price), days: Number(planForm.days), order: Number(planForm.order) };
      if (editingPlan) await api.put(`/admin/plans/${editingPlan.id}`, payload);
      else await api.post("/admin/plans", payload);
      await load();
      setPlanOpen(false);
      toast.success(editingPlan ? "Plan updated" : "Plan created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save plan");
    } finally {
      setSavingPlan(false);
    }
  };
  const deletePlan = async (id) => { await api.delete(`/admin/plans/${id}`); await load(); toast.success("Plan deleted"); };
  const setPF = (k) => (e) => setPlanForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  if (loading) return (<div className="min-h-screen flex justify-center items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>);

  const statCards = stats ? [
    { label: "Businesses", value: stats.businesses, icon: Store },
    { label: "Owners", value: stats.users, icon: Users },
    { label: "Pending payments", value: stats.pending_payments, icon: Clock },
    { label: "Active subs", value: stats.active_subscriptions, icon: CreditCard },
    { label: "Offers", value: stats.offers, icon: Tag },
  ] : [];

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-md bg-primary flex items-center justify-center"><Shield className="w-4 h-4 text-white" /></span>
            <span className="font-display text-lg font-bold tracking-tight">DealWisp · Back Office</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="bg-card border border-white/10 rounded-xl p-5" data-testid={`admin-stat-${s.label}`}>
              <s.icon className="w-5 h-5 text-primary mb-2" />
              <div className="font-display text-2xl font-bold">{s.value}</div>
              <div className="text-xs text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>

        {stats?.users_by_plan && (
          <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-muted-foreground" data-testid="admin-plan-breakdown">
            <span className="overline">By plan</span>
            {Object.entries(stats.users_by_plan).map(([plan, n]) => (
              <span key={plan} className="px-2.5 py-1 rounded-full bg-card border border-white/10 capitalize" data-testid={`plan-breakdown-${plan}`}>
                {plan}: <strong className="text-foreground">{n}</strong>
              </span>
            ))}
          </div>
        )}

        <Tabs defaultValue="payments" className="mt-8">
          <TabsList className="bg-card border border-white/10">
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="businesses" data-testid="tab-businesses">Businesses</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Payment settings</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="payments" className="mt-5">
            <div className="relative max-w-sm mb-4">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={paymentQ} onChange={(e) => setPaymentQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchPayments(paymentQ)}
                placeholder="Search by name, email, or reference…" data-testid="payments-search"
                className="pl-9 bg-background border-white/10" />
            </div>
            {payments.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-no-payments">No payment submissions{paymentQ && " match your search"}.</p> : (
              <div className="space-y-3">
                {payments.map((p) => (
                  <div key={p.payment_id} className="bg-card border border-white/10 rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid={`admin-payment-${p.payment_id}`}>
                    <button type="button" className="flex-1 min-w-[200px] text-left" onClick={() => openUserDetail(p.user_id)} data-testid={`open-user-detail-${p.payment_id}`}>
                      <p className="font-medium hover:text-primary">{p.user_name} <span className="text-muted-foreground text-sm">· {p.user_email}</span></p>
                      <p className="text-sm text-muted-foreground capitalize">{p.plan} plan · ${p.amount} {p.reference && `· ref: ${p.reference}`}</p>
                      <p className="text-xs text-muted-foreground">{new Date(p.submitted_at).toLocaleString()}</p>
                    </button>
                    {p.proof_path && <Button size="sm" variant="outline" onClick={() => setProofView(p.proof_path)} className="rounded-full border-white/15" data-testid={`view-proof-${p.payment_id}`}><Eye className="w-4 h-4 mr-1" /> Proof</Button>}
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${p.status === "approved" ? "bg-green-500/20 text-green-300" : p.status === "rejected" ? "bg-red-500/20 text-red-300" : "bg-yellow-500/20 text-yellow-300"}`}>
                      {p.status === "approved" ? <CheckCircle2 className="w-3 h-3" /> : p.status === "rejected" ? <XCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{p.status}
                    </span>
                    {p.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => act(p.payment_id, "approve")} className="rounded-full bg-green-600 hover:bg-green-700" data-testid={`approve-${p.payment_id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => act(p.payment_id, "reject")} className="rounded-full border-white/15" data-testid={`reject-${p.payment_id}`}>Reject</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="businesses" className="mt-5">
            <div className="relative max-w-sm mb-4">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={businessQ} onChange={(e) => setBusinessQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchBusinesses(businessQ)}
                placeholder="Search by business name, slug, or owner email…" data-testid="businesses-search"
                className="pl-9 bg-background border-white/10" />
            </div>
            {businesses.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-no-businesses">No businesses{businessQ ? " match your search" : " yet"}.</p> : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {businesses.map((b) => (
                  <div key={b.business_id} role="button" tabIndex={0}
                    className="bg-card border border-white/10 rounded-xl p-5 cursor-pointer hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary"
                    onClick={() => openUserDetail(b.owner?.user_id)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), openUserDetail(b.owner?.user_id))}
                    data-testid={`admin-business-${b.business_id}`}>
                    <div className="flex items-center gap-3">
                      {b.logo_path ? <img src={fileUrl(b.logo_path)} alt="" className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 rounded bg-secondary/40 flex items-center justify-center"><Store className="w-4 h-4 text-primary" /></div>}
                      <div><p className="font-medium">{b.name}</p><p className="text-xs text-muted-foreground">{b.owner?.email}</p></div>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                      <span>{b.offer_count} offers · {b.published ? "Live" : "Draft"}</span>
                      <a href={`/b/${b.slug}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">/b/{b.slug}</a>
                    </div>
                    <p className="text-xs mt-2 capitalize">Sub: {b.owner?.subscription?.status || "—"}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="plans" className="mt-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">Plans & prices shown to businesses on the billing page.</p>
              <Button onClick={openNewPlan} className="rounded-full bg-primary hover:bg-secondary" data-testid="add-plan-btn"><Plus className="w-4 h-4 mr-1" /> New plan</Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map((p) => (
                <div key={p.id} className="bg-card border border-white/10 rounded-xl p-5" data-testid={`admin-plan-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-display font-semibold capitalize">{p.name}</h4>
                      <p className="text-xs text-muted-foreground">id: {p.id}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEditPlan(p)} data-testid={`edit-plan-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deletePlan(p.id)} className="text-primary" data-testid={`delete-plan-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <div className="mt-3"><span className="font-display text-2xl font-bold">Rs {Number(p.price).toLocaleString()}</span><span className="text-muted-foreground text-sm">{p.interval === "one_time" ? " one-time" : `/${p.interval}`}</span></div>
                  <p className="text-xs text-muted-foreground mt-1">{p.days} days access</p>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {(p.modules || []).length === 0 ? (
                      <span className="text-xs text-muted-foreground">No extra modules</span>
                    ) : (
                      p.modules.map((m) => (
                        <span key={m} className="text-xs px-2 py-0.5 rounded-full bg-secondary/40" data-testid={`plan-${p.id}-module-${m}`}>
                          {MODULES.find((x) => x.id === m)?.label || m}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              ))}
              {plans.length === 0 && <p className="text-sm text-muted-foreground" data-testid="admin-no-plans">No plans yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="reports" className="mt-5" onFocus={undefined}>
            <ReportsPanel />
          </TabsContent>

          <TabsContent value="settings" className="mt-5">
            <div className="bg-card border border-white/10 rounded-xl p-6 max-w-2xl space-y-4">
              <h3 className="font-display font-semibold">Payment collection details</h3>
              <p className="text-xs text-muted-foreground">These details & QR are shown to businesses when they pay for a plan.</p>
              <ImageUpload label="Payment QR code" value={settings.qr_path || ""} onChange={setS("qr_path")} testid="upload-qr" aspect="aspect-square" />
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label className="overline text-muted-foreground">UPI ID (optional)</Label><Input value={settings.upi_id || ""} onChange={setS("upi_id")} data-testid="settings-upi" className="mt-2 bg-background border-white/10" /></div>
                <div><Label className="overline text-muted-foreground">JazzCash number</Label><Input value={settings.jazzcash || ""} onChange={setS("jazzcash")} placeholder="03xx xxxxxxx" data-testid="settings-jazzcash" className="mt-2 bg-background border-white/10" /></div>
                <div><Label className="overline text-muted-foreground">Easypaisa number</Label><Input value={settings.easypaisa || ""} onChange={setS("easypaisa")} placeholder="03xx xxxxxxx" data-testid="settings-easypaisa" className="mt-2 bg-background border-white/10" /></div>
                <div className="md:col-span-2"><Label className="overline text-muted-foreground">IBAN</Label><Input value={settings.iban || ""} onChange={setS("iban")} placeholder="PK00 XXXX 0000 0000 0000 0000" data-testid="settings-iban" className="mt-2 bg-background border-white/10" /></div>
                <div><Label className="overline text-muted-foreground">Bank name</Label><Input value={settings.bank_name || ""} onChange={setS("bank_name")} data-testid="settings-bank" className="mt-2 bg-background border-white/10" /></div>
                <div><Label className="overline text-muted-foreground">Account name</Label><Input value={settings.account_name || ""} onChange={setS("account_name")} data-testid="settings-accname" className="mt-2 bg-background border-white/10" /></div>
                <div><Label className="overline text-muted-foreground">Account number</Label><Input value={settings.account_number || ""} onChange={setS("account_number")} data-testid="settings-accno" className="mt-2 bg-background border-white/10" /></div>
                <div><Label className="overline text-muted-foreground">Branch code / SWIFT</Label><Input value={settings.ifsc || ""} onChange={setS("ifsc")} data-testid="settings-ifsc" className="mt-2 bg-background border-white/10" /></div>
              </div>
              <div><Label className="overline text-muted-foreground">Instructions</Label><Textarea value={settings.instructions || ""} onChange={setS("instructions")} data-testid="settings-instructions" className="mt-2 bg-background border-white/10" /></div>
              <Button onClick={saveSettings} disabled={savingSettings} className="rounded-full bg-primary hover:bg-secondary" data-testid="save-settings-btn">
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save settings</>}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="bg-card border-white/10 max-w-md" data-testid="plan-dialog">
          <DialogHeader><DialogTitle className="font-display">{editingPlan ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!editingPlan && (
              <div><Label className="overline text-muted-foreground">Plan ID (optional)</Label><Input value={planForm.id} onChange={setPF("id")} placeholder="auto if blank" data-testid="plan-id" className="mt-2 bg-background border-white/10" /></div>
            )}
            <div><Label className="overline text-muted-foreground">Name *</Label><Input value={planForm.name} onChange={setPF("name")} placeholder="Pro" data-testid="plan-name" className="mt-2 bg-background border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline text-muted-foreground">Price</Label><Input type="number" value={planForm.price} onChange={setPF("price")} data-testid="plan-price" className="mt-2 bg-background border-white/10" /></div>
              <div><Label className="overline text-muted-foreground">Days access</Label><Input type="number" value={planForm.days} onChange={setPF("days")} data-testid="plan-days" className="mt-2 bg-background border-white/10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline text-muted-foreground">Interval</Label>
                <Select value={planForm.interval} onValueChange={(v) => setPlanForm((f) => ({ ...f, interval: v }))}>
                  <SelectTrigger className="mt-2 bg-background border-white/10" data-testid="plan-interval"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="month">month</SelectItem>
                    <SelectItem value="year">year</SelectItem>
                    <SelectItem value="week">week</SelectItem>
                    <SelectItem value="one_time">one-time payment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="overline text-muted-foreground">Order</Label><Input type="number" value={planForm.order} onChange={setPF("order")} data-testid="plan-order" className="mt-2 bg-background border-white/10" /></div>
            </div>
            <div>
              <Label className="overline text-muted-foreground">Modules included</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">What this plan unlocks — a business without a module genuinely can't use it, so this is the real reason to pick a pricier plan.</p>
              <div className="space-y-2">
                {MODULES.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`plan-module-checkbox-${m.id}`}>
                    <Checkbox
                      checked={planForm.modules.includes(m.id)}
                      onCheckedChange={(checked) => setPlanForm((f) => ({
                        ...f,
                        modules: checked ? [...f.modules, m.id] : f.modules.filter((x) => x !== m.id),
                      }))}
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={savePlan} disabled={savingPlan} className="w-full rounded-full bg-primary hover:bg-secondary" data-testid="save-plan-btn">
              {savingPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save plan"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!proofView} onOpenChange={() => setProofView(null)}>
        <DialogContent className="bg-card border-white/10" data-testid="proof-dialog">
          <DialogHeader><DialogTitle className="font-display">Payment proof</DialogTitle></DialogHeader>
          {proofView && <img src={fileUrl(proofView)} alt="proof" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!userDetail} onOpenChange={() => setUserDetail(null)}>
        <DialogContent className="bg-card border-white/10 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="user-detail-dialog">
          <DialogHeader><DialogTitle className="font-display">Account details</DialogTitle></DialogHeader>
          {userDetailLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : userDetail?.user ? (
            <div className="space-y-5">
              <div>
                <p className="font-medium">{userDetail.user.name}</p>
                <p className="text-sm text-muted-foreground">{userDetail.user.email}</p>
                <p className="text-xs text-muted-foreground mt-1">Joined {new Date(userDetail.user.created_at).toLocaleDateString()}</p>
              </div>

              <div className="bg-background border border-white/10 rounded-lg p-4" data-testid="user-detail-subscription">
                <h4 className="overline text-muted-foreground mb-2">Subscription</h4>
                <p className="text-sm capitalize">{userDetail.subscription.plan || "—"} · {userDetail.subscription.status || "—"}</p>
                {userDetail.subscription.status === "active" && userDetail.subscription.expires_at && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Next payment due {new Date(userDetail.subscription.expires_at).toLocaleDateString()}</p>
                )}
                {userDetail.subscription.status === "trial" && userDetail.subscription.trial_ends_at && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Trial ends {new Date(userDetail.subscription.trial_ends_at).toLocaleDateString()}</p>
                )}
              </div>

              {userDetail.business ? (
                <div className="bg-background border border-white/10 rounded-lg p-4" data-testid="user-detail-business">
                  <div className="flex items-center justify-between">
                    <h4 className="overline text-muted-foreground">Business</h4>
                    <a href={`/b/${userDetail.business.slug}`} target="_blank" rel="noreferrer" className="text-primary text-xs flex items-center gap-1 hover:underline">View page <ExternalLink className="w-3 h-3" /></a>
                  </div>
                  <p className="font-medium mt-1">{userDetail.business.name}</p>
                  <p className="text-xs text-muted-foreground">{userDetail.business.published ? "Live" : "Draft"} · /b/{userDetail.business.slug}</p>
                  {(userDetail.business.phone || userDetail.business.email || userDetail.business.address) && (
                    <p className="text-xs text-muted-foreground mt-2">{[userDetail.business.phone, userDetail.business.email, userDetail.business.address].filter(Boolean).join(" · ")}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> {userDetail.business.offers_count} offers</span>
                    <span className="flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {userDetail.business.total_clicks} total clicks</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No business profile created yet.</p>
              )}

              <div>
                <h4 className="overline text-muted-foreground mb-2">Payment history</h4>
                {userDetail.payments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No payments submitted.</p>
                ) : (
                  <div className="space-y-2">
                    {userDetail.payments.map((p) => (
                      <div key={p.payment_id} className="flex items-center justify-between text-xs bg-background border border-white/10 rounded-lg px-3 py-2">
                        <span className="capitalize">{p.plan} · ${p.amount} · {new Date(p.submitted_at).toLocaleDateString()}</span>
                        <span className={`px-2 py-0.5 rounded-full ${p.status === "approved" ? "bg-green-500/20 text-green-300" : p.status === "rejected" ? "bg-red-500/20 text-red-300" : "bg-yellow-500/20 text-yellow-300"}`}>{p.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
